import React, { useState, useRef, useCallback } from 'react';
import EditeurVisuel from './EditeurVisuel';

interface GeneratedPlatform {
  content: string;
  hashtags: string[];
}

interface GeneratedResult {
  imageUrl: string;
  connectedPlatforms: string[];
  platforms: Record<string, GeneratedPlatform>;
}

const PLATFORM_META: Record<string, { label: string; color: string; icon: string }> = {
  facebook:           { label: 'Facebook',        color: '#1877F2', icon: 'f'  },
  instagram:          { label: 'Instagram',        color: '#E1306C', icon: 'ig' },
  linkedin:           { label: 'LinkedIn',         color: '#0A66C2', icon: 'in' },
  google_my_business: { label: 'Google Business',  color: '#34A853', icon: 'G'  },
};

const CHAR_LIMITS: Record<string, number> = {
  facebook:           63206,
  instagram:          2200,
  linkedin:           3000,
  google_my_business: 1500,
};

const API = '/api/ai';
const h = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

interface Props {
  onClose: () => void;
  onPostCreated: () => void;
}

const CreatePostModal: React.FC<Props> = ({ onClose, onPostCreated }) => {
  const [step, setStep]               = useState<'upload' | 'result'>('upload');
  const [imageFile, setImageFile]     = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [context, setContext]         = useState('');
  const [editeurOuvert, setEditeurOuvert] = useState(false);
  const [generating, setGenerating]   = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [error, setError]             = useState<string | null>(null);

  const [result, setResult]           = useState<GeneratedResult | null>(null);
  const [activePlatform, setActivePlatform] = useState<string>('');
  const [editedContents, setEditedContents] = useState<Record<string, string>>({});
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [publishMode, setPublishMode]   = useState<'now' | 'schedule'>('schedule');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [saving, setSaving]           = useState(false);
  const [dragging, setDragging]       = useState(false);
  const [listening, setListening]     = useState(false);
  const [interimText, setInterimText] = useState('');
  // Un seul mode depuis le retrait de la generation SAFTI manuelle
  const [mode] = useState<'photo'>('photo');

  const fileInputRef   = useRef<HTMLInputElement>(null);
  const recognitionRef = useRef<SpeechRecognition | null>(null);

  /* ── Microphone continuous ── */
  const toggleMic = () => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) { alert('Microphone non supporté par ce navigateur.'); return; }

    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      setInterimText('');
      return;
    }

    const rec = new SpeechRecognition();
    rec.lang = 'fr-FR';
    rec.continuous = true;
    rec.interimResults = true;

    rec.onresult = (e: SpeechRecognitionEvent) => {
      let interim = '';
      let finalChunk = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalChunk += t;
        else interim += t;
      }
      if (finalChunk) {
        setContext(prev => prev ? `${prev} ${finalChunk}` : finalChunk);
        setInterimText('');
      } else {
        setInterimText(interim);
      }
    };

    rec.onend = () => { setListening(false); setInterimText(''); };
    rec.onerror = () => { setListening(false); setInterimText(''); };
    recognitionRef.current = rec;
    rec.start();
    setListening(true);
  };

  /* ── File handling ── */
  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) { setError('Fichier image requis.'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = e => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
    setError(null);
  };

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, []);

  /* ── Apply API result ── */
  const applyResult = (json: any) => {
    setResult(json);
    setActivePlatform(json.connectedPlatforms[0]);
    const contents: Record<string, string> = {};
    for (const p of json.connectedPlatforms) {
      const pd = json.platforms[p];
      contents[p] = pd ? `${pd.content}${pd.hashtags?.length ? '\n\n' + pd.hashtags.map((t: string) => `#${t.replace(/^#/, '')}`).join(' ') : ''}` : '';
    }
    setEditedContents(contents);
    setSelectedPlatforms(json.connectedPlatforms);
    setStep('result');
  };

  /* ── Generate from photo ── */
  const generate = async (isRegen = false) => {
    if (!imageFile) { setError('Veuillez sélectionner une image.'); return; }
    if (isRegen) setRegenerating(true); else setGenerating(true);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('image', imageFile);
      if (context) formData.append('context', context);
      const res = await fetch(`${API}/generate-from-photo`, { method: 'POST', headers: h(), body: formData });
      const json = await res.json();
      if (!json.success) { setError(json.error || 'Erreur de génération.'); return; }
      applyResult(json);
    } catch {
      setError('Erreur réseau. Vérifiez votre connexion.');
    } finally {
      if (isRegen) setRegenerating(false); else setGenerating(false);
    }
  };

  /* ── Save / Publish ── */
  const savePost = async () => {
    if (!selectedPlatforms.length) { setError('Sélectionnez au moins une plateforme.'); return; }
    setSaving(true);
    try {
      let scheduledFor: string | null = null;
      if (publishMode === 'now') {
        scheduledFor = new Date().toISOString();
      } else if (scheduleDate) {
        scheduledFor = `${scheduleDate}T${scheduleTime}`;
      }
      for (const platform of selectedPlatforms) {
        await fetch(`${API}/create-post`, {
          method: 'POST',
          headers: { ...h(), 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content:      editedContents[platform] || '',
            imageUrl:     result?.imageUrl || null,
            platforms:    [platform],
            scheduledFor,
          }),
        });
      }
      onPostCreated();
      onClose();
    } catch {
      setError('Erreur lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Character counter helpers ── */
  const charCount = (editedContents[activePlatform] || '').length;
  const charLimit = CHAR_LIMITS[activePlatform] ?? null;
  const charOver  = charLimit !== null && charCount > charLimit;
  const charWarn  = charLimit !== null && charCount > charLimit * 0.9 && !charOver;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className="text-lg">✦</span>
            <h2 className="font-bold text-gray-900 text-[15px]">
              {step === 'upload' ? 'Créer un post avec IA' : 'Votre post généré'}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">

          {/* ── STEP 1 : Upload ── */}
          {step === 'upload' && (
            <div className="p-6 space-y-4">

              {/* ── PHOTO MODE ── */}
              {mode === 'photo' && (
                <>
                  {/* Drop zone */}
                  <div
                    onDragOver={e => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl cursor-pointer transition-all overflow-hidden
                      ${dragging ? 'border-violet-400 bg-violet-50' : 'border-gray-200 hover:border-violet-300 hover:bg-gray-50'}
                      ${imagePreview ? 'h-64' : 'h-48 flex items-center justify-center'}`}
                  >
                    {imagePreview ? (
                      <>
                        <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center">
                          <span className="opacity-0 hover:opacity-100 text-white text-sm font-semibold bg-black/50 px-3 py-1.5 rounded-full transition-opacity">
                            Changer de photo
                          </span>
                        </div>
                      </>
                    ) : (
                      <div className="text-center">
                        <div className="text-4xl mb-2">📷</div>
                        <p className="text-sm font-medium text-gray-600">Glissez votre photo ici</p>
                        <p className="text-xs text-gray-400 mt-1">ou cliquez pour parcourir</p>
                        <p className="text-[11px] text-violet-400 mt-2 font-medium">Image obligatoire pour générer le post</p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={e => e.target.files?.[0] && handleFile(e.target.files[0])}
                    />
                  </div>

                  {imageFile && (
                    <button type="button" onClick={() => setEditeurOuvert(true)} className="w-full py-2 text-sm font-semibold rounded-lg border border-violet-200 text-violet-700 hover:bg-violet-50">
                      Habiller l'image : phrases, badges, autocollants
                    </button>
                  )}
                  {editeurOuvert && imageFile && (
                    <EditeurVisuel photo={imageFile} onFermer={() => setEditeurOuvert(false)} onValider={f => { handleFile(f); setEditeurOuvert(false); }} />
                  )}

                  {/* Contexte + micro */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Consignes de rédaction <span className="text-gray-300 font-normal normal-case">(optionnel)</span>
                      </label>
                      <button
                        type="button"
                        onClick={toggleMic}
                        title={listening ? "Arrêter l'enregistrement" : 'Dicter une consigne'}
                        className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                          listening
                            ? 'bg-red-500 text-white animate-pulse'
                            : 'bg-gray-100 text-gray-500 hover:bg-violet-100 hover:text-violet-600'
                        }`}
                      >
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-3.5 h-3.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8"/>
                        </svg>
                        {listening ? 'Écoute...' : 'Dicter'}
                      </button>
                    </div>
                    <textarea
                      value={context}
                      onChange={e => setContext(e.target.value)}
                      placeholder="Ex: promotion de printemps, avant/après, nouveau service... ou dictez avec le micro"
                      rows={2}
                      className={`w-full border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 transition-colors ${
                        listening ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-violet-300'
                      }`}
                    />
                    {/* Affichage texte interim micro */}
                    {interimText && (
                      <p className="text-xs text-red-400 italic mt-1 px-1">
                        🎤 {interimText}
                      </p>
                    )}
                  </div>
                </>
              )}

              {error && <p className="text-red-500 text-xs">{error}</p>}
            </div>
          )}

          {/* ── STEP 2 : Résultats ── */}
          {step === 'result' && result && (
            <div className="p-6 space-y-4">

              {/* Aperçu image grand format */}
              {imagePreview && (
                <div className="relative rounded-xl overflow-hidden h-48 bg-gray-100">
                  <img src={imagePreview} alt="preview" className="w-full h-full object-cover" />
                  <div className="absolute bottom-2 right-2">
                    <span className="text-[10px] bg-black/50 text-white px-2 py-0.5 rounded-full">Image du post</span>
                  </div>
                </div>
              )}

              {/* Sélection des plateformes */}
              <div>
                <p className="text-xs text-gray-500 mb-2">Sélectionnez les plateformes à publier :</p>
                <div className="flex flex-wrap gap-2">
                  {result.connectedPlatforms.map(p => {
                    const meta = PLATFORM_META[p];
                    const selected = selectedPlatforms.includes(p);
                    return (
                      <button
                        key={p}
                        onClick={() => setSelectedPlatforms(prev =>
                          prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
                        )}
                        style={{ borderColor: selected ? meta?.color : '#e5e7eb', color: selected ? meta?.color : '#9ca3af', background: selected ? `${meta?.color}12` : 'white' }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all"
                      >
                        <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold flex-shrink-0"
                          style={{ background: meta?.color }}>{meta?.icon}</span>
                        {meta?.label}
                        {selected && <span className="text-[10px]">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Onglets plateformes + édition */}
              <div className="border border-gray-100 rounded-xl overflow-hidden">
                <div className="flex border-b border-gray-100 overflow-x-auto">
                  {result.connectedPlatforms.map(p => {
                    const meta = PLATFORM_META[p];
                    const pCount = (editedContents[p] || '').length;
                    const pLimit = CHAR_LIMITS[p] ?? null;
                    const pOver  = pLimit !== null && pCount > pLimit;
                    return (
                      <button
                        key={p}
                        onClick={() => setActivePlatform(p)}
                        style={{ borderBottomColor: activePlatform === p ? meta?.color : 'transparent', color: activePlatform === p ? meta?.color : '#9ca3af' }}
                        className="flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap flex-shrink-0"
                      >
                        <span className="w-4 h-4 rounded-full flex items-center justify-center text-white text-[8px] font-bold"
                          style={{ background: meta?.color }}>{meta?.icon}</span>
                        {meta?.label}
                        {pOver && <span className="text-red-500 text-[10px]">⚠</span>}
                      </button>
                    );
                  })}
                </div>
                <div className="p-3">
                  <textarea
                    value={editedContents[activePlatform] || ''}
                    onChange={e => setEditedContents(prev => ({ ...prev, [activePlatform]: e.target.value }))}
                    rows={6}
                    className="w-full text-sm text-gray-800 resize-none focus:outline-none leading-relaxed"
                    placeholder="Contenu du post..."
                  />
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[10px] text-gray-300">
                      {charLimit ? `Limite recommandée : ${charLimit.toLocaleString()} car.` : ''}
                    </span>
                    <span className={`text-[11px] font-medium tabular-nums ${
                      charOver ? 'text-red-500' : charWarn ? 'text-amber-500' : 'text-gray-300'
                    }`}>
                      {charCount.toLocaleString()}{charLimit ? ` / ${charLimit.toLocaleString()}` : ''} car.
                      {charOver && ' — Trop long !'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Publication : maintenant ou planifier */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">
                  Publication
                </label>
                <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm font-medium">
                  <button
                    onClick={() => setPublishMode('now')}
                    className={`flex-1 py-2 transition-colors ${publishMode === 'now' ? 'bg-violet-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  >
                    Publier maintenant
                  </button>
                  <button
                    onClick={() => setPublishMode('schedule')}
                    className={`flex-1 py-2 transition-colors ${publishMode === 'schedule' ? 'bg-violet-600 text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}
                  >
                    Planifier
                  </button>
                </div>
                {publishMode === 'schedule' && (
                  <div className="mt-2 flex gap-2">
                    <input
                      type="date"
                      value={scheduleDate}
                      onChange={e => setScheduleDate(e.target.value)}
                      min={new Date().toISOString().slice(0, 10)}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                    <input
                      type="time"
                      value={scheduleTime}
                      onChange={e => setScheduleTime(e.target.value)}
                      className="w-28 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-300"
                    />
                  </div>
                )}
              </div>

              {error && <p className="text-red-500 text-xs">{error}</p>}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 justify-between items-center">
          {step === 'upload' ? (
            <>
              <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                Annuler
              </button>
              {mode === 'photo' && (
                <button
                  onClick={() => generate(false)}
                  disabled={!imageFile || generating}
                  className="flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {generating
                    ? <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Génération en cours...</>
                    : <><span>✦</span> Générer mon post</>
                  }
                </button>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2">
                <button onClick={() => setStep('upload')} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700">
                  ← Retour
                </button>
                {mode === 'photo' && (
                  <button
                    onClick={() => generate(true)}
                    disabled={regenerating}
                    title="Régénérer le texte avec la même photo"
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-violet-200 text-violet-600 text-xs font-semibold hover:bg-violet-50 disabled:opacity-50 transition-all"
                  >
                    {regenerating
                      ? <><div className="w-3.5 h-3.5 border-2 border-violet-300 border-t-violet-600 rounded-full animate-spin" /> En cours...</>
                      : <><span>↺</span> Régénérer</>
                    }
                  </button>
                )}
              </div>
              <button
                onClick={savePost}
                disabled={saving || !selectedPlatforms.length}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {saving ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Sauvegarde...</>
                ) : publishMode === 'now' ? '⚡ Publier maintenant' : scheduleDate ? '📅 Planifier' : '💾 Sauvegarder en brouillon'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatePostModal;
